import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Loader } from 'lucide-react';
import { searchAddress, GeocodingResult } from '../lib/geocoding';
import { useDebounce } from 'use-debounce';

interface LocationSearchProps {
  onLocationSelect: (location: {
    address: string;
    lat: number;
    lng: number;
  }) => void;
  defaultValue?: string;
}

const LocationSearch: React.FC<LocationSearchProps> = ({ onLocationSelect, defaultValue = '' }) => {
  const [query, setQuery] = useState(defaultValue);
  const [debouncedQuery] = useDebounce(query, 500);
  const [results, setResults] = useState<GeocodingResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchResults = async () => {
      if (!debouncedQuery.trim()) {
        setResults([]);
        return;
      }

      setLoading(true);
      try {
        const searchResults = await searchAddress(debouncedQuery);
        setResults(searchResults || []); // Ensure we always have an array
      } catch (error) {
        console.error('Error fetching address suggestions:', error);
        setResults([]); // Set empty array on error
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [debouncedQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleResultClick = (result: GeocodingResult) => {
    const formattedAddress = result.display_name;
    setQuery(formattedAddress);
    setShowResults(false);
    onLocationSelect({
      address: formattedAddress,
      lat: parseFloat(result.lat),
      lng: parseFloat(result.lon)
    });
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setShowResults(true);
          }}
          onFocus={() => setShowResults(true)}
          className="pl-10 w-full p-2 border rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          placeholder="Addresse eingeben..."
        />
        {loading && (
          <div className="absolute right-3 top-3">
            <Loader className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        )}
      </div>

      {showResults && results && results.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white rounded-md shadow-lg max-h-60 overflow-auto">
          {results.map((result, index) => (
            <button
              key={index}
              className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
              onClick={() => handleResultClick(result)}
            >
              <div className="flex items-start">
                <MapPin className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                <span className="ml-2">{result.display_name}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocationSearch;